import React, { useRef, useState } from 'react';
import { motion, useInView, AnimatePresence } from 'framer-motion';
import { X, ZoomIn } from 'lucide-react';

const galleryImages = [
  {
    id: 1,
    src: 'https://images.unsplash.com/photo-1655558846882-fa55132d6c20?q=85&w=800&auto=format&fit=crop',
    alt: 'MMA Training Session',
    category: 'MMA',
  },
  {
    id: 2,
    src: 'https://images.unsplash.com/photo-1711825044889-371d0cdf5fe1?q=85&w=800&auto=format&fit=crop',
    alt: 'Punching Bag Training',
    category: 'Boxing',
  },
  {
    id: 3,
    src: 'https://images.unsplash.com/photo-1711825049856-24a93ae9de23?q=85&w=800&auto=format&fit=crop',
    alt: 'Sparring in the Ring',
    category: 'Sparring',
  },
  {
    id: 4,
    src: 'https://images.unsplash.com/photo-1773289336870-862f5eb371fc?q=85&w=800&auto=format&fit=crop',
    alt: 'Muay Thai Kick Training',
    category: 'Muay Thai',
  },
  {
    id: 5,
    src: 'https://images.unsplash.com/photo-1696454411278-a64de1369e83?q=85&w=800&auto=format&fit=crop',
    alt: 'Ring Sparring Session',
    category: 'Sparring',
  },
  {
    id: 6,
    src: 'https://images.unsplash.com/photo-1773289338631-17455d5c3b73?q=85&w=800&auto=format&fit=crop',
    alt: 'Boxing Training',
    category: 'Boxing',
  },
  {
    id: 7,
    src: 'https://images.unsplash.com/photo-1644594570589-ef85bd03169f?q=85&w=800&auto=format&fit=crop',
    alt: 'BJJ Ground Work',
    category: 'BJJ',
  },
  {
    id: 8,
    src: 'https://images.unsplash.com/photo-1758778932703-7bfaaf1c42cd?q=85&w=800&auto=format&fit=crop',
    alt: 'Kickboxing Gym Training',
    category: 'Kickboxing',
  },
  {
    id: 9,
    src: 'https://images.unsplash.com/photo-1542937306-d1056fdd367a?q=85&w=800&auto=format&fit=crop',
    alt: 'Jiu Jitsu Grappling',
    category: 'Grappling',
  },
];

const GallerySection = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  const [selectedImage, setSelectedImage] = useState(null);

  return (
    <section
      id="gallery"
      ref={ref}
      className="relative py-24 md:py-32 bg-[#0A0A0A]"
      data-testid="gallery-section"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-[#DC2626] text-sm font-bold tracking-[0.3em] uppercase">
            Gallery
          </span>
          <h2 className="font-['Oswald'] text-4xl md:text-5xl font-bold text-white mt-4 mb-6">
            Training In<br />
            <span className="text-[#DC2626]">Action</span>
          </h2>
          <p className="text-neutral-400 max-w-xl mx-auto">
            Witness the intensity and dedication of our fighters during training sessions.
          </p>
        </motion.div>

        {/* Masonry Gallery Grid */}
        <div className="columns-1 md:columns-2 lg:columns-3 gap-4 space-y-4">
          {galleryImages.map((image, index) => (
            <motion.div
              key={image.id}
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.5, delay: index * 0.1 }}
              className="gallery-item relative group break-inside-avoid cursor-pointer"
              onClick={() => setSelectedImage(image)}
              data-testid={`gallery-item-${image.id}`}
            >
              <div className="relative overflow-hidden border border-[#262626]">
                <img
                  src={image.src}
                  alt={image.alt}
                  className="w-full h-auto object-cover grayscale hover:grayscale-0 transition-all duration-500"
                />
                {/* Overlay */}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                  <ZoomIn size={32} className="text-white" />
                </div>
                {/* Category badge */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black to-transparent">
                  <span className="text-[#DC2626] text-xs font-bold tracking-wider uppercase">
                    {image.category}
                  </span>
                  <p className="text-white text-sm mt-1">{image.alt}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-black/95 flex items-center justify-center p-4"
            onClick={() => setSelectedImage(null)}
            data-testid="gallery-lightbox"
          >
            <button
              onClick={() => setSelectedImage(null)}
              className="absolute top-6 right-6 text-white hover:text-[#DC2626] transition-colors"
              data-testid="close-lightbox-btn"
            >
              <X size={32} />
            </button>
            <motion.img
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              src={selectedImage.src}
              alt={selectedImage.alt}
              className="max-w-full max-h-[85vh] object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <div className="absolute bottom-6 left-1/2 -translate-x-1/2 text-center">
              <span className="text-[#DC2626] text-sm font-bold tracking-wider uppercase">
                {selectedImage.category}
              </span>
              <p className="text-white mt-1">{selectedImage.alt}</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </section>
  );
};

export default GallerySection;
